import React from 'react';

// TacticalMarketCard — v2.4 Mobile Tactical Sprint.
//
// Replaces MarketFeedRow on mobile (< 768 px). Keeps the same macro
// information budget (title, favourite, outsider, pool, vouch status)
// but laid out as a card with a thumb-friendly primary CTA at the
// foot. Context-aware:
//
//   ACTIVE   → [ PLEDGE ]   opens the Focus Modal via onAction
//   PENDING  → [ VOUCH  ]   calls onAction to cast the citizen vouch
//
// v2.4.2 — the 48 px "Glow Only" Mini-Penumbra in the header has been
// excised. The Political Market Table stays focused on odds and
// party names; the Sovereign Glow doughnut lives exclusively in the
// Main Action column (.penumbra-host).

export interface MiniOutcome {
  id:     string;
  label:  string;
  price:  number;
  color?: string;
}

interface Props {
  marketId:        string;
  title:           string;
  lens?:           string;
  ocVolume:        number;
  favorite?:       MiniOutcome;
  outsider?:       MiniOutcome;
  outcomes?:       MiniOutcome[];   // still accepted so call sites don't break; no longer rendered as a mini chart.
  momentumActive?: boolean;
  active?:         boolean;
  pending?:        boolean;
  vouchCount?:     number;
  vouchTarget?:    number;
  canVouch?:       boolean;         // citizen+ AND not the creator
  // I-065 settled rail (DECISIONS.md 2026-05-17). Same semantics as
  // the desktop MarketFeedRow's settled props — when true, replaces
  // the favourite/outsider body with a SETTLED block, opacity drops,
  // border neutralises, CTA becomes [VIEW FINAL] (still opens focus
  // modal to view the frozen doughnut).
  settled?:        boolean;
  winningOutcome?: string | null;
  settledAt?:      string | null;
  // I-068 Brief 2 mobile parity — same NULL-safe / pending-suppressed
  // / settled-muted contract as MarketFeedRow. Wraps to two lines on
  // narrow viewports (no nowrap) so longer oracle names like
  // "European Broadcasting Union" stay readable rather than getting
  // ellipsis-clipped in the card header.
  oracleDisplayText?:      string;
  oracleClassLabel?:       string;
  holdbackHoursOverride?:  number;
  onAction:        (marketId: string) => void;
}

function formatOC(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  if (n >= 100)       return n.toFixed(0);
  return n.toFixed(1);
}

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatSettledAtUTC(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day  = d.getUTCDate();
  const mon  = MONTHS_SHORT[d.getUTCMonth()];
  const hh   = String(d.getUTCHours()).padStart(2, '0');
  const mm   = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day} ${mon} ${hh}:${mm} UTC`;
}

export default function TacticalMarketCard({
  marketId, title, lens, ocVolume, favorite, outsider,
  momentumActive = false, active = false,
  pending = false, vouchCount, vouchTarget, canVouch = false,
  settled = false, winningOutcome, settledAt,
  oracleDisplayText, oracleClassLabel, holdbackHoursOverride,
  onAction,
}: Props): React.JSX.Element {
  // Pending markets suppress the oracle line entirely (operational
  // noise before vouch clears); settled rows render it at reduced
  // opacity inherited from the card's settled style. NULL-safe:
  // system-audit and any future NULL-oracle markets just omit the
  // line — no placeholder, no warning.
  const showOracleLine = !pending && !!oracleDisplayText;
  const borderColor = pending
    ? '#334155'
    : settled
      ? '#475569'
      : active
        ? '#93c5fd'
        : momentumActive
          ? '#a78bfa'
          : '#1e293b';

  // 2026-05-21 picker-and-legibility brief: the previous `[ PLEDGE ]`
  // CTA on the active card was mislabelled — its onClick fired the
  // SAME handler the desktop row's whole-row tap uses (onActivate →
  // setActiveId + focus the doughnut). It did not pledge; pledging
  // lives in the focus view it opens. The card body itself was inert,
  // so the natural "tap the market to open it" gesture did nothing —
  // the reported "market picker isn't functional" symptom.
  //
  // Fix: ACTIVE and SETTLED cards are now whole-card `<button>`s that
  // call onAction directly (matching MarketFeedRow's tap-the-whole-row
  // behaviour). The mislabelled CTA is gone — pledging happens in the
  // focus view, as it always did.
  //
  // PENDING is different: handleVouchPending genuinely casts a vouch
  // (engine/mintedMarkets.vouchForMarket), so the `[ VOUCH ]` label is
  // honest. Pending stays as an inert `<article>` body (matching the
  // desktop row's `disabled={pending}` no-op tap) with the inner
  // `[ VOUCH ]` button as the only live element. Real-button-inside-
  // button would be invalid HTML, which is the other reason to keep
  // the pending wrapper a non-button.
  const isInteractiveCard = !pending; // active and settled — tap-to-focus
  const cardAriaLabel = pending
    ? title
    : settled
      ? `View final state of ${title}`
      : `Open ${title}`;

  const cardStyle: React.CSSProperties = {
    position:       'relative',
    display:        'flex',
    flexDirection:  'column',
    gap:            10,
    padding:        '12px 14px',
    borderRadius:   8,
    border:         `1px ${pending ? 'dashed' : 'solid'} ${borderColor}`,
    background:     pending ? '#060c16' : settled ? '#0a0f17' : active ? '#0d1c2e' : '#0a121d',
    color:          '#e2e8f0',
    fontFamily:     'monospace',
    textAlign:      'left',
    width:          '100%',
    opacity:        pending || settled ? 0.85 : 1,
    cursor:         isInteractiveCard ? 'pointer' : 'default',
    transition:     'border-color 0.15s, background 0.15s',
  };

  const cardBody = (
    <>
      {/* Row 1 — Title + lens + oracle line */}
      <header style={{ minWidth: 0 }}>
        <div style={{
          color:         pending ? '#94a3b8' : settled ? '#cbd5e1' : '#e2e8f0',
          fontSize:      14,
          fontWeight:    800,
          letterSpacing: '0.01em',
          lineHeight:    1.25,
          overflow:      'hidden',
          textOverflow:  'ellipsis',
          display:       '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
        }}>
          {title}
        </div>
        {lens && (
          <div style={{
            marginTop:     4,
            color:         '#64748b',
            fontSize:      8,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
          }}>
            {lens}{pending ? ' · STABILIZING' : settled ? ' · SETTLED' : ''}
          </div>
        )}
        {showOracleLine && (
          <div style={{
            marginTop:  4,
            color:      '#94a3b8',
            fontSize:   10,
            lineHeight: 1.4,
            opacity:    settled ? 0.65 : 0.85,
          }}>
            Resolves per {oracleDisplayText}
            {oracleClassLabel ? ` · ${oracleClassLabel}` : ''}
            {typeof holdbackHoursOverride === 'number'
              ? ` · ${holdbackHoursOverride}h holdback`
              : ''}
          </div>
        )}
      </header>

      {/* Row 2 — body. Three shapes: pending vouch-count / settled
          winner block / active POOL · FAVOURITE · OUTSIDER triplet. */}
      {pending ? (
        <div style={{
          display:        'flex',
          flexDirection:  'column',
          alignItems:     'center',
          gap:            4,
          padding:        '8px 0 4px',
        }}>
          <div style={{ color: '#fcd34d', fontSize: 10, fontWeight: 800, letterSpacing: '0.22em' }}>
            STABILIZING · PENDING VOUCH
          </div>
          <div style={{ color: '#e2e8f0', fontSize: 22, fontWeight: 700, letterSpacing: '0.05em' }}>
            {typeof vouchCount === 'number' ? vouchCount : 0}
            <span style={{ color: '#475569', fontSize: 16, margin: '0 4px' }}>/</span>
            {typeof vouchTarget === 'number' ? vouchTarget : '—'}
          </div>
          <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em' }}>
            CITIZEN VOUCHES TO ACTIVATE
          </div>
        </div>
      ) : settled ? (
        // I-065 SETTLED body — winning outcome readout + SETTLED
        // timestamp. Allowed to wrap on narrow cards so long outcome
        // names don't ellipsis-truncate; desktop's nowrap+ellipsis was
        // a grid-cell constraint that doesn't apply here.
        <div style={{
          display:        'flex',
          flexDirection:  'column',
          alignItems:     'flex-start',
          gap:            4,
          padding:        '6px 0 2px',
        }}>
          <div style={{
            color:         '#cbd5e1',
            fontSize:      14,
            fontWeight:    800,
            letterSpacing: '0.02em',
            whiteSpace:    'normal',
            wordBreak:     'break-word',
            lineHeight:    1.3,
            maxWidth:      '100%',
          }}>
            {winningOutcome ?? '—'}
            <span style={{ color: '#94a3b8', fontWeight: 600, marginLeft: 6 }}>won</span>
          </div>
          <div style={{ color: '#64748b', fontSize: 9 }}>
            <span style={{ letterSpacing: '0.22em' }}>SETTLED</span>
            {settledAt ? <span>{` · ${formatSettledAtUTC(settledAt)}`}</span> : null}
          </div>
        </div>
      ) : (
        // Active triplet — POOL · FAVOURITE · OUTSIDER as one coherent
        // group, value-on-top + label-underneath, mirroring the
        // desktop row's reading rhythm. Replaces the prior 2-col
        // fav/outsider grid + bottom POOL strip + divider, which
        // scattered the three facts across three vertical zones.
        // Outcome names allowed to wrap (no nowrap/ellipsis) — there's
        // vertical room and the full name is more useful than a
        // clipped one on a card.
        <div style={{
          display:        'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)',
          gap:            12,
          padding:        '2px 0',
          alignItems:     'flex-start',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              color:         '#7dd3fc',
              fontSize:      13,
              fontWeight:    800,
              letterSpacing: '0.02em',
              lineHeight:    1.3,
            }}>
              {formatOC(ocVolume)} OC
            </div>
            <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em', marginTop: 2 }}>
              POOL
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{
              color:         favorite?.color ?? '#86efac',
              fontSize:      12,
              fontWeight:    800,
              letterSpacing: '0.02em',
              whiteSpace:    'normal',
              wordBreak:     'break-word',
              lineHeight:    1.3,
            }}>
              {favorite ? `${favorite.label} @ ${favorite.price.toFixed(2)}` : '—'}
            </div>
            <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em', marginTop: 2 }}>
              FAVOURITE
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{
              color:         outsider?.color ?? '#fcd34d',
              fontSize:      12,
              fontWeight:    800,
              letterSpacing: '0.02em',
              whiteSpace:    'normal',
              wordBreak:     'break-word',
              lineHeight:    1.3,
            }}>
              {outsider ? `${outsider.label} @ ${outsider.price.toFixed(2)}` : '—'}
            </div>
            <div style={{ color: '#78350f', fontSize: 8, letterSpacing: '0.22em', marginTop: 2 }}>
              OUTSIDER
            </div>
          </div>
        </div>
      )}

      {/* Pending-only inner button — the only path that performs a
          real action on this surface (handleVouchPending). Active and
          settled cards have no inner CTA; the whole-card tap on the
          outer <button> is the action. */}
      {pending && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); if (canVouch) onAction(marketId); }}
          disabled={!canVouch}
          aria-label={`Vouch for ${title}`}
          title={!canVouch ? 'Citizen tier required to vouch.' : undefined}
          style={{
            marginTop:      4,
            width:          '100%',
            minHeight:      48,
            padding:        '12px 14px',
            borderRadius:   6,
            border:         `1px solid ${canVouch ? '#fcd34d' : '#1e293b'}`,
            background:     canVouch ? 'transparent' : '#0a0f1c',
            color:          canVouch ? '#fcd34d' : '#475569',
            fontFamily:     'monospace',
            fontSize:       13,
            fontWeight:     800,
            letterSpacing:  '0.28em',
            cursor:         canVouch ? 'pointer' : 'not-allowed',
            transition:     'border-color 0.15s, color 0.15s, background 0.15s',
          }}
        >
          [ VOUCH ]
        </button>
      )}
    </>
  );

  // Active / settled wrap as a native <button> so the whole card is
  // the tap target, exactly like MarketFeedRow. Pending wraps as an
  // <article> so the inner [ VOUCH ] button remains a valid button-
  // inside-a-non-button (HTML disallows nested buttons) and the body
  // stays inert — matching the desktop pending row that's
  // `disabled={pending}` and no-op on tap.
  if (isInteractiveCard) {
    return (
      <button
        type="button"
        onClick={() => onAction(marketId)}
        aria-label={cardAriaLabel}
        style={cardStyle}
      >
        {cardBody}
      </button>
    );
  }
  return (
    <article aria-label={cardAriaLabel} style={cardStyle}>
      {cardBody}
    </article>
  );
}
